import { getPageFixture } from '@web-studio/core-fixtures';
import { useMemo } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { MatrixCanvas } from '@/components/MatrixCanvas';
import { usePageFixtureEditor } from '@/hooks/useFixtureBlocks';
import { useLabCaptureMode } from '@/hooks/useLabCaptureMode';
import { parsePageSearchParams } from '@/lib/matrixState';

/**
 * Legacy `/matrix/page/:pageId` route.
 * Capture ingress stays here (marketplace Playwright). Interactive use redirects to Package layout.
 */
export function PageLabPage() {
  const { pageId: routePageId } = useParams<{ pageId: string }>();
  const [searchParams] = useSearchParams();
  const captureMode = useLabCaptureMode();

  const state = useMemo(() => {
    const base = parsePageSearchParams(searchParams);
    if (routePageId) {
      return { ...base, pageFixtureId: routePageId };
    }
    return base;
  }, [searchParams, routePageId]);

  const page = getPageFixture(state.pageFixtureId);
  const { blocks } = usePageFixtureEditor(state.pageFixtureId);

  if (!captureMode && page) {
    const next = new URLSearchParams(searchParams);
    next.set('vertical', page.vertical);
    next.set('tier', page.tier);
    next.set('page', page.id);
    if (!next.get('theme')) next.set('theme', page.defaultThemeId);
    next.delete('capture');
    return <Navigate to={`/matrix/tiers?${next.toString()}`} replace />;
  }

  if (captureMode) {
    return page && blocks.length > 0 ? (
      <div className="lab-capture-stage">
        <MatrixCanvas
          blocks={blocks}
          locale={state.locale}
          themeId={state.themeId}
          colorsetId={state.colorsetId}
          spacingEnum={state.spacingEnum}
          viewport={state.viewport}
        />
      </div>
    ) : (
      <p className="text-sm">Unknown page fixture: {state.pageFixtureId}</p>
    );
  }

  return <p className="text-sm">Unknown page fixture: {state.pageFixtureId}</p>;
}
